import React, { useState, useEffect, useRef } from 'react';
import { Input } from 'antd';
import { validateAndLimitDate } from '../../utils/dateUtils';

/**
 * 统一日期范围输入组件
 *
 * 合并 DepartmentView（鼠标选择跟踪）和 EditableDataTable（autoFocus、键盘事件）的功能
 * 输入格式：YYYY-MM-DD ~ YYYY-MM-DD
 *
 * @param {Object} props
 * @param {string[]} props.value - [startDate, endDate]
 * @param {function} props.onChange - 值变更回调，接收 [start, end]
 * @param {function} [props.onBlur] - 失焦回调
 * @param {function} [props.onPressEnter] - 回车回调
 * @param {function} [props.onKeyDown] - 键盘事件回调
 * @param {string} [props.placeholder] - 占位文本
 * @param {Object} [props.style] - 样式
 * @param {boolean} [props.autoFocus=false] - 自动聚焦
 * @param {string} [props.data-cell-key] - 表格内编辑标识
 */
const DateRangeInput = ({
  value,
  onChange,
  onBlur,
  onPressEnter,
  onKeyDown,
  placeholder,
  style,
  autoFocus = false,
  'data-cell-key': dataCellKey,
}) => {
  const inputRef = useRef(null);
  const isSelectingRef = useRef(false);

  const rangeValue = value && Array.isArray(value) ? value : ['', ''];
  const startValue = rangeValue[0] || '';
  const endValue = rangeValue[1] || '';

  const [localStartValue, setLocalStartValue] = useState(startValue);
  const [localEndValue, setLocalEndValue] = useState(endValue);

  useEffect(() => {
    setLocalStartValue(startValue);
    setLocalEndValue(endValue);
  }, [startValue, endValue]);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const displayValue = localStartValue && localEndValue
    ? `${localStartValue} ~ ${localEndValue}`
    : (localStartValue || localEndValue || '');

  const handleChange = (e) => {
    const raw = e.target.value;
    const parts = raw.split('~').map(s => s.trim());
    if (parts.length === 1) {
      setLocalStartValue(parts[0]);
      setLocalEndValue('');
      onChange([parts[0], '']);
    } else if (parts.length >= 2) {
      setLocalStartValue(parts[0]);
      setLocalEndValue(parts[1]);
      onChange([parts[0], parts[1]]);
    }
  };

  const validateAndFormatDates = () => {
    let start = localStartValue;
    let end = localEndValue;

    if (start) {
      const parts = start.split('-');
      if (parts.length === 3) {
        const v = validateAndLimitDate(parts[0], parts[1], parts[2]);
        start = `${v.year}-${v.month}-${v.day}`;
      }
    }
    if (end) {
      const parts = end.split('-');
      if (parts.length === 3) {
        const v = validateAndLimitDate(parts[0], parts[1], parts[2]);
        end = `${v.year}-${v.month}-${v.day}`;
      }
    }

    setLocalStartValue(start);
    setLocalEndValue(end);
    onChange([start, end]);
  };

  const handleBlur = (e) => {
    if (isSelectingRef.current) {
      isSelectingRef.current = false;
      if (inputRef.current) inputRef.current.focus();
      return;
    }
    validateAndFormatDates();
    if (onBlur) onBlur(e);
  };

  const handlePressEnter = (e) => {
    validateAndFormatDates();
    if (onPressEnter) onPressEnter(e);
  };

  return (
    <Input
      ref={inputRef}
      value={displayValue}
      onChange={handleChange}
      onBlur={handleBlur}
      onPressEnter={handlePressEnter}
      onKeyDown={onKeyDown}
      style={style}
      placeholder={placeholder}
      maxLength={23}
      data-cell-key={dataCellKey}
      autoFocus={autoFocus}
      allowClear
    />
  );
};

export default DateRangeInput;
